import { useEffect, useMemo, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { collection, doc, onSnapshot } from "firebase/firestore";
import { auth, db } from "./firebase";
import { calculateRecordFee, canonicalTeamName, isSalariedTeam } from "./utils/feeRules";
import { endOfBangkokDay, getSalaryPayday } from "./utils/payoutCalc";
import "./TeamOverview.css";

const fmt = (value) =>
  Number(value || 0).toLocaleString("th-TH", { maximumFractionDigits: 2 });

const getRecordDate = (record) => {
  if (record?.createdAt?.toDate) return record.createdAt.toDate();
  if (record?.date) {
    const d = new Date(record.date);
    if (!Number.isNaN(d.getTime())) return d;
  }
  if (record?.pressDate) {
    const d = new Date(record.pressDate);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
};

const formatDate = (date) => {
  if (!date) return "-";
  return date.toLocaleString("th-TH", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const monthLabel = (date) =>
  date.toLocaleDateString("th-TH", { month: "long", year: "numeric" });

const isOwnerRecord = (record) => canonicalTeamName(record?.teamName) === "ยย";
const isSettledRecord = (record) => isOwnerRecord(record) || !!record?.isPaid || !!record?.settlementId;

export default function TeamOverview({ setTab }) {
  const [user, setUser] = useState(null);
  const [teamName, setTeamName] = useState("");
  const [realName, setRealName] = useState("");
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [now] = useState(() => new Date());

  useEffect(() => {
    let unsubProfile = null;
    let unsubRecords = null;

    const unsubAuth = onAuthStateChanged(auth, (currentUser) => {
      if (unsubProfile) unsubProfile();
      if (unsubRecords) unsubRecords();

      if (!currentUser) {
        setUser(null);
        setRecords([]);
        setTeamName("");
        setRealName("");
        setLoading(false);
        return;
      }

      setUser(currentUser);
      setLoading(true);

      unsubProfile = onSnapshot(doc(db, "users", currentUser.uid), (snap) => {
        const profile = snap.exists() ? snap.data() : {};
        setTeamName(canonicalTeamName(profile?.teamName || profile?.name || ""));
        setRealName(String(profile?.realName || "").trim());
      });

      unsubRecords = onSnapshot(
        collection(db, `user-press-fee/${currentUser.uid}/records`),
        (snapshot) => {
          setRecords(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
          setLoading(false);
        },
        (error) => {
          console.error("โหลดข้อมูลแดชบอร์ดทีมไม่สำเร็จ", error);
          setLoading(false);
        }
      );
    });

    return () => {
      unsubAuth();
      if (unsubProfile) unsubProfile();
      if (unsubRecords) unsubRecords();
    };
  }, []);

  const normalizedRecords = useMemo(
    () =>
      records
        .map((record) => ({
          ...record,
          __date: getRecordDate(record),
          __fee: calculateRecordFee(record),
        }))
        .sort(
          (a, b) =>
            (b.__date?.getTime?.() || 0) - (a.__date?.getTime?.() || 0)
        ),
    [records]
  );

  const monthRecords = useMemo(
    () =>
      normalizedRecords.filter(
        (record) =>
          record.__date &&
          record.__date.getFullYear() === now.getFullYear() &&
          record.__date.getMonth() === now.getMonth()
      ),
    [normalizedRecords, now]
  );

  const monthStats = useMemo(
    () =>
      monthRecords.reduce(
        (acc, record) => {
          acc.net += record.__fee.net;
          acc.qty += Number(record.quantity || 0);
          acc.count += 1;
          return acc;
        },
        { net: 0, qty: 0, count: 0 }
      ),
    [monthRecords]
  );

  const pendingStats = useMemo(
    () =>
      normalizedRecords.reduce(
        (acc, record) => {
          if (!isSettledRecord(record)) {
            acc.net += record.__fee.net;
            acc.qty += Number(record.quantity || 0);
            acc.count += 1;
            // พนักงานเงินเดือน: แยกยอดที่ถึงรอบโอนแล้ว กับยอดที่รอรอบวันที่ 26 ถัดไป
            const payday = getSalaryPayday(record.createdAt);
            if (!payday || payday <= endOfBangkokDay(now)) {
              acc.dueNet += record.__fee.net;
              acc.dueCount += 1;
            } else {
              acc.laterNet += record.__fee.net;
              acc.laterCount += 1;
            }
          }
          return acc;
        },
        { net: 0, qty: 0, count: 0, dueNet: 0, dueCount: 0, laterNet: 0, laterCount: 0 }
      ),
    [normalizedRecords, now]
  );
  const salaried = isSalariedTeam(teamName);

  const paidThisMonth = useMemo(
    () => monthRecords.filter((record) => isSettledRecord(record)).length,
    [monthRecords]
  );

  const latestRecords = normalizedRecords.slice(0, 5);

  const firstName =
    teamName ||
    user?.displayName?.trim()?.split(/\s+/)?.[0] ||
    "ทีมงาน";

  return (
    <div className="team-overview-page">
      <div className="team-overview-shell">
        <header className="team-overview-header">
          <div>
            <span className="overview-eyebrow">TEAM DASHBOARD</span>
            <h1>สวัสดี {firstName}</h1>
            {realName && <div className="overview-real-name">{realName}</div>}
            <p>ภาพรวมค่ากดและสถานะการโอนของคุณในที่เดียว</p>
          </div>
          <div className="overview-month-chip">
            <span className="overview-live-dot" />
            {monthLabel(now)}
          </div>
        </header>

        {loading ? (
          <div className="overview-loading-card">
            <span className="overview-loader" />
            กำลังโหลดข้อมูลของคุณ...
          </div>
        ) : (
          <>
            <section className="overview-hero-grid">
              <article className="overview-income-card">
                <div className="overview-income-topline">
                  <div>
                    <span className="overview-card-kicker">รายได้สุทธิเดือนนี้</span>
                    <div className="overview-income-value">฿{fmt(monthStats.net)}</div>
                  </div>
                  <div className="overview-wallet-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none">
                      <path d="M4 7.5h14.5A1.5 1.5 0 0 1 20 9v9a2 2 0 0 1-2 2H5a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3h11.5a1.5 1.5 0 0 1 0 3H5a1 1 0 0 0-1 1v.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M16 12h4v4h-4a2 2 0 1 1 0-4Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>

                <div className="overview-income-meta">
                  <div>
                    <span>จำนวนบัตร</span>
                    <strong>{fmt(monthStats.qty)} ใบ</strong>
                  </div>
                  <div>
                    <span>รายการ</span>
                    <strong>{fmt(monthStats.count)} รายการ</strong>
                  </div>
                  <div>
                    <span>โอนแล้ว</span>
                    <strong>{fmt(paidThisMonth)} รายการ</strong>
                  </div>
                </div>
              </article>

              <article className={`overview-pending-card ${pendingStats.count === 0 ? "is-clear" : ""}`}>
                <div className="overview-pending-head">
                  <div className="overview-pending-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none">
                      <path d="M12 8v4l2.5 1.5M21 12a9 9 0 1 1-3.2-6.9" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M21 4v5h-5" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <span className={`pending-status-badge ${pendingStats.count === 0 ? "clear" : ""}`}>
                    {pendingStats.count === 0 ? "เคลียร์แล้ว" : "รอโอน"}
                  </span>
                </div>
                <span className="overview-card-kicker">ค่ากดสุทธิรอโอน (ทุกรอบ)</span>
                <div className="overview-pending-value">฿{fmt(pendingStats.net)}</div>
                <p>
                  {pendingStats.count === 0
                    ? "ตอนนี้ไม่มีรายการที่ค้างโอน"
                    : `${fmt(pendingStats.count)} รายการ · ${fmt(pendingStats.qty)} ใบ`}
                </p>
                {salaried && pendingStats.count > 0 && (
                  <p>
                    ถึงรอบโอนแล้ว ฿{fmt(pendingStats.dueNet)} ({fmt(pendingStats.dueCount)} รายการ)
                    {pendingStats.laterCount > 0 && ` · รอรอบ 26 ถัดไป ฿${fmt(pendingStats.laterNet)} (${fmt(pendingStats.laterCount)} รายการ)`}
                  </p>
                )}
                {pendingStats.count > 0 && <p>ยังไม่รวมรายได้พิเศษ / ค่าคอมที่แอดมินเพิ่มให้</p>}
              </article>
            </section>

            <section className="overview-quick-section">
              <div className="overview-section-head">
                <div>
                  <span className="overview-section-kicker">QUICK ACTIONS</span>
                  <h2>ทำอะไรต่อดี?</h2>
                </div>
              </div>

              <div className="overview-action-grid">
                <button
                  type="button"
                  className="overview-action-card primary"
                  onClick={() => setTab?.("tracker")}
                >
                  <span className="overview-action-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none">
                      <path d="M12 20h9" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"/>
                      <path d="m16.5 3.5 4 4L8 20H4v-4L16.5 3.5Z" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                  <span className="overview-action-copy">
                    <strong>กรอกค่ากด</strong>
                    <small>เพิ่มรายการใหม่</small>
                  </span>
                  <span className="overview-action-arrow">→</span>
                </button>

                <button
                  type="button"
                  className="overview-action-card"
                  onClick={() => setTab?.("history")}
                >
                  <span className="overview-action-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none">
                      <path d="M7 3h10a2 2 0 0 1 2 2v16l-7-3-7 3V5a2 2 0 0 1 2-2Z" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                  <span className="overview-action-copy">
                    <strong>ดูประวัติ</strong>
                    <small>เช็กยอดและสถานะโอน</small>
                  </span>
                  <span className="overview-action-arrow">→</span>
                </button>
              </div>
            </section>

            <section className="overview-recent-section">
              <div className="overview-section-head recent-head">
                <div>
                  <span className="overview-section-kicker">RECENT ACTIVITY</span>
                  <h2>รายการล่าสุด</h2>
                </div>
                {normalizedRecords.length > 5 && (
                  <button type="button" className="overview-text-button" onClick={() => setTab?.("history")}>
                    ดูทั้งหมด
                  </button>
                )}
              </div>

              {latestRecords.length === 0 ? (
                <div className="overview-empty-state">
                  <div className="overview-empty-icon" aria-hidden="true">＋</div>
                  <strong>ยังไม่มีรายการค่ากด</strong>
                  <p>เมื่อกรอกค่ากดครั้งแรก รายการล่าสุดจะมาแสดงตรงนี้</p>
                  <button type="button" onClick={() => setTab?.("tracker")}>กรอกรายการแรก</button>
                </div>
              ) : (
                <div className="overview-recent-list">
                  {latestRecords.map((record) => (
                    <div className="overview-recent-row" key={record.id}>
                      <div className="overview-event-mark" aria-hidden="true">
                        {String(record.eventName || "งาน").trim().charAt(0).toUpperCase() || "•"}
                      </div>
                      <div className="overview-recent-main">
                        <div className="overview-recent-title-row">
                          <strong>{record.eventName || "ไม่ระบุชื่องาน"}</strong>
                          <span className={`overview-paid-chip ${isSettledRecord(record) ? "paid" : "pending"}`}>
                            {isOwnerRecord(record) ? "ไม่ต้องโอน" : isSettledRecord(record) ? "โอนแล้ว" : "รอโอน"}
                          </span>
                        </div>
                        <div className="overview-recent-meta">
                          <span>{record.zone || record.priceLabel || "ไม่ระบุโซน"}</span>
                          <span>•</span>
                          <span>{fmt(record.quantity)} ใบ</span>
                          <span>•</span>
                          <span>{formatDate(record.__date)}</span>
                        </div>
                      </div>
                      <div className="overview-recent-amount">฿{fmt(record.__fee.net)}</div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}
